import {
  applySnapshot,
  detach,
  flow,
  getParent,
  getRoot,
  types,
} from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import { FileModel, IFileModel } from "../../components/fileupload/FileModel";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import {
  AgreementDetailModel,
  IAgreementDetailModel,
} from "../agreement/AgreementModel";
import { PlanHeaderModel } from "../share/PlanModel";
import TableAmountDataModel, {
  ITableAmountDataModel,
} from "../share/TableAmountDataModel";
import financialReportAPI from "./FinancialReportService";
import { incomeStatementFieldName, balanceSheetFieldName,cashFlowFieldName,orgLocalDebtEstimationFieldName,govLocalDebtEstimationFieldName,financialRatioFieldName } from "../../constants";
const million = 1000000;
import { convertToCurrencyNumber } from "../../utils/convert-to-currency-number";

export const BalanceSheetModel = types
  .model("BalanceSheetModel", {
    year: types.optional(types.string, ""),
    asset: customtypes.optional(types.number, 0),
    currentAsset: customtypes.optional(types.number, 0),
    fixedAsset: customtypes.optional(types.number, 0),
    liabilityEquity: types.optional(types.number, 0),
    liability: types.optional(types.number, 0),
    currentLiability: customtypes.optional(types.number, 0),
    longTermLiability: customtypes.optional(types.number, 0),
    equity: customtypes.optional(types.number, 0),
  })
  .views((self: any) => ({
    get assetView() {
      return (self.currentAsset + self.fixedAsset) / million;
    },
    get liabilityEquityView() {
      return (
        (self.currentLiability + self.longTermLiability + self.equity) / million
      );
    },
    get liabilityView() {
      return (self.currentLiability + self.longTermLiability) / million;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IBalanceSheetModel = typeof BalanceSheetModel.Type;

export const IncomeStatementModel = types
  .model("IncomeStatementModel", {
    year: types.optional(types.string, ""),
    revenue: types.optional(types.number, 0),
    expense: types.optional(types.number, 0),
    glossProfit: types.optional(types.number, 0),
    ebitda: types.optional(types.number, 0),
    netProfit: types.optional(types.number, 0),
  })
  .views((self: any) => ({
    get grossProfitView() {
      return (self.revenue - self.expense) / million;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IIncomeStatementModel = typeof IncomeStatementModel.Type;

export const CashflowModel = types
  .model("CashflowModel", {
    year: types.optional(types.string, ""),
    operation: customtypes.optional(types.number, 0),
    investing: customtypes.optional(types.number, 0),
    financing: customtypes.optional(types.number, 0),
    netCashFlow: types.optional(types.number, 0),
    cashBalanceBeginning: customtypes.optional(types.number, 0),
    cashBalanceEnding: customtypes.optional(types.number, 0),
  })
  .views((self: any) => ({
    get netCashFlowView() {
      return (self.operation + self.investing + self.financing) / million;
    },
    get cashBalanceEndingView() {
      return (
        (self.cashBalanceBeginning + self.netCashFlowView * million) / million
      );
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type ICashflowModel = typeof CashflowModel.Type;

export const DebtEstimateModel = types
  .model("DebtEstimateModel", {
    year: types.optional(types.string, ""),
    outStandingDebt: types.optional(types.number, 0),
    disbursement: types.optional(types.number, 0),
    principle: types.optional(types.number, 0),
    interest: types.optional(types.number, 0),
    netCashFlow: types.optional(types.number, 0),
    fee: types.optional(types.number, 0),
  })
  .views((self: any) => ({
    get repayment() {
      return self.principle + self.interest;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setCurrency: (value: any) => {
      const parent: IDebtEstimateListModel = getParent(self, 2);
      parent.setField({ fieldname: "currency", value });
    },
  }));
export type IDebtEstimateModel = typeof DebtEstimateModel.Type;

export const DebtEstimateListModel = types
  .model("DebtEstimateListModel", {
    currency: types.optional(types.string, ""),
    orgForeignDebtEstimation: types.optional(
      types.array(DebtEstimateModel),
      []
    ),
  })
  .views((self: any) => ({
    get repayment() {
      return self.principle + self.interest;
    },
    get foreignEstimateAllDebtDataTable() {
      const tableArray = [];
      const outstandingDebts: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.outstandingDebt",
        editMode: true,
        financialType: "fead",
        fieldname: "outStandingDebt",
      };
      const disbursements: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.disbursement",
        editMode: true,
        financialType: "fead",
        fieldname: "disbursement",
      };
      const repayments: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.repayment",
      };
      const principles: any = {
        level: 2,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.principle",
        editMode: true,
        financialType: "fead",
        fieldname: "principle",
      };
      const interests: any = {
        level: 2,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.interest",
        editMode: true,
        financialType: "fead",
        fieldname: "interest",
      };
      const netCashFlows: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.netCashFlow",
        editMode: true,
        financialType: "fead",
        fieldname: "netCashFlow",
      };
      self.orgForeignDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          outstandingDebts[`year${index + 1}`] = de.outStandingDebt / million;
          disbursements[`year${index + 1}`] = de.disbursement / million;
          repayments[`year${index + 1}`] = de.repayment / million;
          principles[`year${index + 1}`] = de.principle / million;
          interests[`year${index + 1}`] = de.interest / million;
          netCashFlows[`year${index + 1}`] = de.netCashFlow / million;
        }
      );
      tableArray.push(
        outstandingDebts,
        disbursements,
        repayments,
        principles,
        interests,
        netCashFlows
      );

      return tableArray;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IDebtEstimateListModel = typeof DebtEstimateListModel.Type;

export const GovDebtEstimateModel = types
  .model("GovDebtEstimateModel", {
    year: types.optional(types.string, ""),
    principle: types.optional(types.number, 0),
    interest: types.optional(types.number, 0),
    fee: types.optional(types.number, 0),
  })
  .views((self: any) => ({}))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setCurrency: (value: any) => {
      const parent: IGovDebtEstimateListModel = getParent(self, 2);
      parent.setField({ fieldname: "currency", value });
    },
  }));
export type IGovDebtEstimateModel = typeof GovDebtEstimateModel.Type;

export const GovDebtEstimateListModel = types
  .model("DebtEstimateListModel", {
    currency: types.optional(types.string, ""),
    govForeignDebtEstimation: types.optional(
      types.array(GovDebtEstimateModel),
      []
    ),
  })
  .views((self: any) => ({
    get foreignEstimateGovernmentDebtDataTable() {
      const tableArray = [];
      const principles: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.principle",
        editMode: true,
        financialType: "fgd",
        fieldname: "principle",
      };
      const interests: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.interest",
        editMode: true,
        financialType: "fgd",
        fieldname: "interest",
      };
      const fees: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.fee",
        editMode: true,
        financialType: "fgd",
        fieldname: "fee",
      };
      self.govForeignDebtEstimation.forEach(
        (de: IGovDebtEstimateModel, index: number) => {
          principles[`year${index + 1}`] = de.principle / million;
          interests[`year${index + 1}`] = de.interest / million;
          fees[`year${index + 1}`] = de.fee / million;
        }
      );
      tableArray.push(principles, interests, fees);
      return tableArray;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IGovDebtEstimateListModel = typeof GovDebtEstimateListModel.Type;

export const DebtSummaryModel = types
  .model("DebtSummaryModel", {
    currentDebt: types.optional(types.array(AgreementDetailModel), []),
    orgLocalDebtEstimation: types.optional(types.array(DebtEstimateModel), []),
    orgForeignDebtEstimationList: types.optional(
      types.array(DebtEstimateListModel),
      []
    ),
    govLocalDebtEstimation: types.optional(types.array(DebtEstimateModel), []),
    govForeignDebtEstimationList: types.optional(
      types.array(GovDebtEstimateListModel),
      []
    ),
    // เพิ่มฟิลด์ ORGTYPE
    ORGTYPE: types.maybeNull(types.number)
  })
  .views((self: any) => ({
    get totalLocalCurrentDebt() {
      let guarantee = 0;
      let notGuarantee = 0;
      self.localCurrentDebt.forEach((item: IAgreementDetailModel) => {
        if (item.isGuarantee === true) {
          guarantee += item.outStandingDebtTHB;
        }else{
          if (self.ORGTYPE != 20143) 
            {
              notGuarantee += item.outStandingDebtTHB;  
            } 
        }
      });
      return [guarantee / million, notGuarantee / million];
    },
    get totalForeignCurrentDebt() {
      let guarantee = 0;
      let notGuarantee = 0;
      self.foreignCurrentDebt.forEach((item: IAgreementDetailModel) => {
        if (item.isGuarantee === true) {
          guarantee += item.outStandingDebtTHB;
        } else {
            if (self.ORGTYPE != 20143) 
            {
              notGuarantee += item.outStandingDebtTHB;
            }
        }
      });
      return [guarantee / million, notGuarantee / million];
    },
    get localCurrentDebt() {
      return self.currentDebt.filter((cb: IAgreementDetailModel) => {
        return cb.sourceType === "L";
      });
    },
    get foreignCurrentDebt() {
      return self.currentDebt.filter((cb: IAgreementDetailModel) => {
        return cb.sourceType === "F";
      });
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setORGTYPE: (value: number) => {
      self.ORGTYPE = value;
    },
    addOrgForeignDebtEstimationList: (item: IDebtEstimateListModel) => {
      self.orgForeignDebtEstimationList.push(item);
    },
    addGovForeignDebtEstimationList: (item: IGovDebtEstimateListModel) => {
      self.govForeignDebtEstimationList.push(item);
    },
  }));
export type IDebtSummaryModel = typeof DebtSummaryModel.Type;

export const FinancialRatioModel = types
  .model("FinancialRatioModel", {
    year: types.optional(types.string, ""),
    dsrc: types.optional(types.number, 0),
    leRatio: types.optional(types.number, 0),
    laRatio: types.optional(types.number, 0),
    caclRatio: types.optional(types.number, 0),
    npfaRatio: types.optional(types.number, 0),
    npaRatio: types.optional(types.number, 0),
    nprRatio: types.optional(types.number, 0),
    ebida: types.optional(types.number, 0),
    debt: types.optional(types.number, 0), // ภาระหนี้(เงินต้น+ดอกเบี้ยที่ครบกำหนดชำระ)
    pDebt: types.optional(types.number, 0), // ภาระหนี้(เงินต้นที่ครบกำหนดชำระ)
    pDebtG: types.optional(types.number, 0), // ภาระหนี้(เงินต้นที่ครบกำหนดชำระ: รัฐบาลรับภาระ)
    pDebtO: types.optional(types.number, 0), // ภาระหนี้(เงินต้นที่ครบกำหนดชำระ: หน่วยงานรับภาระ)
    iDebt: types.optional(types.number, 0), // ภาระหนี้(ดอกเบี้ยที่ครบกำหนดชำระ)
    iDebtG: types.optional(types.number, 0), // ภาระหนี้(ดอกเบี้ยที่ครบกำหนดชำระ: รัฐบาลรับภาระ)
    iDebtO: types.optional(types.number, 0), // ภาระหนี้(ดอกเบี้ยที่ครบกำหนดชำระ: หน่วยงานรับภาระ)
    govDebt: types.optional(types.number, 0), // ภาระเงินต้นและดอกเบี้ยที่รัฐบาลรับภาระ
    pGovDebt: types.optional(types.number, 0), // ภาระเงินต้นที่รัฐบาลรับภาระ
    iGovDebt: types.optional(types.number, 0), // ภาระดอกเบี้ยที่รัฐบาลรับภาระ
    liability: types.optional(types.number, 0),
    equity: types.optional(types.number, 0),
    asset: types.optional(types.number, 0),
    currentAsset: types.optional(types.number, 0),
    currentLiability: types.optional(types.number, 0),
    netProfit: types.optional(types.number, 0),
    fixedAsset: types.optional(types.number, 0),
    revenue: types.optional(types.number, 0),
  })
  .views((self: any) => ({
    get govDebtView() {
      return (self.pGovDebt + self.iGovDebt) / million;
    },
    get debtView() {
      return (self.pDebt + self.pDebtG+ self.pDebtO+self.iDebt+self.iDebtG+self.iDebtO) / million;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IFinancialRatioModel = typeof FinancialRatioModel.Type;


export const DSCRNoteModel = types
  .model("DSCRNoteModel", {
    dsrcNoteID: types.maybe(types.number),
    year: types.optional(types.union(types.string, types.number), ""),
    dscr: types.optional(types.union(types.string, types.number), ""),
    reason: types.optional(types.string, ""),
    solution: types.optional(types.string, ""),
    progressUpdate: types.optional(types.string, ""),
  })
  .views((self: any) => ({
    get dscrView() {
      return self.dscr === 0 ? "" : self.dscr;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setDscr: (value: string) => {
      const [first, second] = value.split(".");
      self.dscr = second === "" ? value : +value;
    },
    // remove: () => {
    //   const root: IFinancialReportModel = getRoot(self);
    //   root.removeDSCRReason(self);
    // },
    remove: flow(function* () {
      const root: any = getRoot(self);
      yield root.deleteDscr(self);
    }),
  }));
export type IDSCRNoteModel = typeof DSCRNoteModel.Type;

export const EditFinRatioModel = types
  .model("EditFinRatioModel", {
    financialRatio: types.optional(types.array(FinancialRatioModel), []),
    remark: customtypes.optional(types.string, ""),
  })
  .views((self: any) => ({
    get getLemark() {
      return self.remark ;
    }
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    }
  }));
export type IEditFinRatioModel = typeof EditFinRatioModel.Type;

export const FinancialReportModel = types
  .model("FinancialReportModel", {
    planHeader: types.optional(PlanHeaderModel, {}),
    balanceSheet: types.optional(types.array(BalanceSheetModel), []),
    incomeStatements: types.optional(types.array(IncomeStatementModel), []),
    cashFlows: types.optional(types.array(CashflowModel), []),
    debtSummary: types.optional(DebtSummaryModel, {}),
    financialRatio: types.optional(types.array(FinancialRatioModel), []),
    editFinRatio: types.optional(EditFinRatioModel, {}),
    dscrNotes: types.optional(types.array(DSCRNoteModel), []),
    govLocalDebtEstimation: types.optional(types.array(DebtEstimateModel), []),
    coffers: customtypes.optional(types.array(TableAmountDataModel), [
      {
        code: "001",
        title: "ทุนที่เรียกชำระแล้ว",
      },
      {
        code: "002",
        title: "ส่วนล้ำมูลค่าหุ้น",
      },
      {
        code: "003",
        title: "ทุนสำรอง",
      },
      {
        code: "004",
        title: "เงินที่ได้รับจากการออกใบสำคัญแสดงสิทธิเพื่อซื้อหุ้น",
      },
      {
        code: "005",
        title: "เงินสำรองที่ได้จากกำไรสุทธิ",
      },
      {
        code: "006",
        title: "กำไรสุทธิคงเหลือจากการจัดสรรแล้ว",
      },
      {
        code: "007",
        title: "เงินสำรองจากการตีราคาสินทรัพย์",
      },
      {
        code: "008",
        title:
          "เงินที่ได้รับจากการออกตราสารแสดงสิทธิในหนี้ระยะยาวเกินห้าปีที่มีสิทธิด้อยกว่าเจ้าหนี้สามัญ",
      },
    ]),
    attachFiles: customtypes.optional(types.array(FileModel), []),
    error: types.optional(ErrorModel, {}),
    alert: types.optional(MessageModel, {}),
    loading: types.optional(types.boolean, false),
  })
  .views((self: any) => ({
    get netCash() {
      return (self.operation + self.investing + self.financing) / million;
    },
    get cashEnding() {
      return (self.cashBeginning - self.netCash) / million;
    },
    get balanceSheetDataTable() {
      const tableArray = [];
      const assets: any = {
        label: "financial.balanceSheetTable.asset",
        level: 1,
        editMode: false,
        highlight: true,
        financialType: "bs",
        fieldname: "asset",
      };
      const currentAssets: any = {
        label: "financial.balanceSheetTable.currentAssets",
        level: 2,
        editMode: true,
        highlight: false,
        financialType: "bs",
        fieldname: "currentAsset",
      };
      const fixedAssets: any = {
        label: "financial.balanceSheetTable.permanentAsset",
        level: 2,
        editMode: true,
        highlight: false,
        financialType: "bs",
        fieldname: "fixedAsset",
      };
      const liabilityEquities: any = {
        label: "financial.balanceSheetTable.liabilitiesEquity",
        level: 1,
        editMode: false,
        highlight: true,
        financialType: "bs",
        fieldname: "liabilityEquity",
      };
      const liabilities: any = {
        label: "financial.balanceSheetTable.debt",
        level: 2,
        editMode: false,
        highlight: false,
        financialType: "bs",
        fieldname: "liability",
      };
      const currentLiabilities: any = {
        label: "financial.balanceSheetTable.totalCurrentLiabilities",
        level: 3,
        editMode: true,
        highlight: false,
        financialType: "bs",
        fieldname: "currentLiability",
      };
      const longtermLiabilities: any = {
        label: "financial.balanceSheetTable.longTermLiabilities",
        level: 3,
        editMode: true,
        highlight: false,
        financialType: "bs",
        fieldname: "longTermLiability",
      };
      const equities: any = {
        label: "financial.balanceSheetTable.equity",
        level: 2,
        editMode: true,
        highlight: false,
        financialType: "bs",
        fieldname: "equity",
      };
      self.balanceSheet.forEach((bs: IBalanceSheetModel, index: number) => {
        assets[`year${index + 1}`] = bs.assetView
          ? bs.assetView
          : bs.asset / million;
        currentAssets[`year${index + 1}`] = bs.currentAsset / million;
        fixedAssets[`year${index + 1}`] = bs.fixedAsset / million;
        liabilityEquities[`year${index + 1}`] = bs.liabilityEquityView
          ? bs.liabilityEquityView
          : bs.liabilityEquity / million;
        liabilities[`year${index + 1}`] = bs.liabilityView
          ? bs.liabilityView
          : bs.liability / million;
        currentLiabilities[`year${index + 1}`] = bs.currentLiability / million;
        longtermLiabilities[`year${index + 1}`] =
          bs.longTermLiability / million;
        equities[`year${index + 1}`] = bs.equity / million;
      });
      tableArray.push(
        assets,
        currentAssets,
        fixedAssets,
        liabilityEquities,
        liabilities,
        currentLiabilities,
        longtermLiabilities,
        equities
      );
      return tableArray;
    },
    get financialReportDataTableExcel() {
      const tableArray = [];
      const space: any = {
        fieldname: "",
      };

      const nameBalance: any = {
        fieldname: balanceSheetFieldName.preliminaryBalanceSheetProjections,
      };
      const colBalance: any = {
        fieldname: balanceSheetFieldName.list,
      };
      const assets: any = {
        fieldname: balanceSheetFieldName.asset,
      };
      const currentAssets: any = {
        fieldname: balanceSheetFieldName.currentAsset,
      };
      const fixedAssets: any = {
        fieldname: balanceSheetFieldName.fixedAsset,
      };
      const liabilityEquities: any = {
        fieldname: balanceSheetFieldName.liabilityEquity,
      };
      const liabilities: any = {
        fieldname: balanceSheetFieldName.liability,
      };
      const currentLiabilities: any = {
        fieldname: balanceSheetFieldName.currentLiability,
      };
      const longtermLiabilities: any = {
        fieldname: balanceSheetFieldName.longTermLiability,
      };
      const equities: any = {
        fieldname: balanceSheetFieldName.equity,
      };
      self.balanceSheet.forEach((bs: IBalanceSheetModel, index: number) => {
        space[`year${index + 1}`]="";
        nameBalance[`year${index + 1}`]="";
        colBalance[`year${index + 1}`]=bs.year;
        assets[`year${index + 1}`] = bs.assetView
          ? convertToCurrencyNumber(bs.assetView * million,2)
          : convertToCurrencyNumber(bs.asset,2) ;
        currentAssets[`year${index + 1}`] = convertToCurrencyNumber(bs.currentAsset,2) ;
        fixedAssets[`year${index + 1}`] = convertToCurrencyNumber(bs.fixedAsset,2) ;
        liabilityEquities[`year${index + 1}`] = bs.liabilityEquityView
          ? convertToCurrencyNumber(bs.liabilityEquityView * million,2)
          : convertToCurrencyNumber(bs.liabilityEquity,2);
        liabilities[`year${index + 1}`] = bs.liabilityView
          ? convertToCurrencyNumber(bs.liabilityView * million,2)
          : convertToCurrencyNumber(bs.liability,2);
        currentLiabilities[`year${index + 1}`] = convertToCurrencyNumber(bs.currentLiability,2);
        longtermLiabilities[`year${index + 1}`] =
        convertToCurrencyNumber(bs.longTermLiability,2);
        equities[`year${index + 1}`] = convertToCurrencyNumber(bs.equity,2);
      });
      tableArray.push(
        space,
        nameBalance,
        colBalance,
        assets,
        currentAssets,
        fixedAssets,
        liabilityEquities,
        liabilities,
        currentLiabilities,
        longtermLiabilities,
        equities
      );

      //Income
    
      const nameIncome: any = {
        fieldname: incomeStatementFieldName.basicProfitLossAndEstimates,
      };
      const colIncome: any = {
        fieldname: incomeStatementFieldName.list,
      };
      const revenues: any = {
        fieldname: incomeStatementFieldName.revenue,
      };
      const expenses: any = {
        fieldname: incomeStatementFieldName.expense ,
      };
      const grossProfits: any = {
        fieldname: incomeStatementFieldName.glossProfit,
      };
      const ebitdas: any = {
        fieldname: incomeStatementFieldName.ebitda,
      };
      const netProfits: any = {
        fieldname: incomeStatementFieldName.netProfit,
      };
      self.incomeStatements.forEach(
        (is: IIncomeStatementModel, index: number) => {
          space[`year${index + 1}`]="";
          nameIncome[`year${index + 1}`]="";
          colIncome[`year${index + 1}`]=is.year;
          revenues[`year${index + 1}`] = convertToCurrencyNumber(is.revenue,2);
          expenses[`year${index + 1}`] = convertToCurrencyNumber(is.expense,2);
          grossProfits[`year${index + 1}`] = is.grossProfitView
            ? convertToCurrencyNumber(is.grossProfitView * million ,2)
            : convertToCurrencyNumber(is.glossProfit,2);
          ebitdas[`year${index + 1}`] = convertToCurrencyNumber(is.ebitda,2);
          netProfits[`year${index + 1}`] = convertToCurrencyNumber(is.netProfit,2);
        }
      );
      tableArray.push(space,nameIncome,colIncome,revenues, expenses, grossProfits, ebitdas, netProfits);
      
      //cashFlowTable
      const nameCashFlow: any = {
        fieldname: cashFlowFieldName.preliminaryCashFlowStatementsEstimates,
      };
      const colCashFlow: any = {
        fieldname: cashFlowFieldName.list,
      };
      const operations: any = {
        fieldname: cashFlowFieldName.operation,
      };
      const investings: any = {
        fieldname: cashFlowFieldName.investing,
      };
      const financings: any = {
        fieldname: cashFlowFieldName.financing,
      };
      const cashflows: any = {
        fieldname: cashFlowFieldName.cashFlow,
      };
      const netCashflows: any = {
        fieldname: cashFlowFieldName.netCashFlow,
      };
      const cashBalanceBeginnings: any = {
        fieldname: cashFlowFieldName.cashBalanceBeginning,
      };
      const cashBalanceEndings: any = {
        fieldname:cashFlowFieldName.cashBalanceEnding,
      };
      self.cashFlows.forEach((is: ICashflowModel, index: number) => {
        space[`year${index + 1}`]="";
        nameCashFlow[`year${index + 1}`]="";
        colCashFlow[`year${index + 1}`]=is.year;
        operations[`year${index + 1}`] = convertToCurrencyNumber(is.operation,2);
        investings[`year${index + 1}`] = convertToCurrencyNumber(is.investing,2);
        financings[`year${index + 1}`] = convertToCurrencyNumber(is.financing,2);
        cashflows[`year${index + 1}`] = "";
        netCashflows[`year${index + 1}`] = is.netCashFlowView
          ? convertToCurrencyNumber(is.netCashFlowView*million,2)
          : convertToCurrencyNumber(is.netCashFlow,2);
        cashBalanceBeginnings[`year${index + 1}`] =
        convertToCurrencyNumber(is.cashBalanceBeginning,2);
        cashBalanceEndings[`year${index + 1}`] = is.cashBalanceEndingView
          ? convertToCurrencyNumber(is.cashBalanceEndingView*million,2)
          : convertToCurrencyNumber(is.cashBalanceEnding,2);
      });
      tableArray.push(
        space,
        nameCashFlow,
        colCashFlow,
        operations,
        investings,
        financings,
        cashflows,
        netCashflows,
        cashBalanceBeginnings,
        cashBalanceEndings
      );

      //orgLocalDebtEstimation
      const nameOrgDebt: any = {
        fieldname: orgLocalDebtEstimationFieldName.estimateAllDebt,
      };
      const loanSources: any = {
        fieldname: orgLocalDebtEstimationFieldName.domesticLoanSources,
      };
      const colOrgDebt: any = {
        fieldname: orgLocalDebtEstimationFieldName.list,
      };
      const outstandingDebts: any = {
        fieldname: orgLocalDebtEstimationFieldName.outstandingDebt,
      };
      const disbursements: any = {
        fieldname: orgLocalDebtEstimationFieldName.disbursement,
      };
      const repayments: any = {
        fieldname: orgLocalDebtEstimationFieldName.repayment,
      };
      const principles: any = {
        fieldname: orgLocalDebtEstimationFieldName.principle,
      };
      const interests: any = {
        fieldname: orgLocalDebtEstimationFieldName.interest,
      };
      const netCashFlows: any = {
        fieldname: orgLocalDebtEstimationFieldName.netCashFlow,
      };
      self.debtSummary.orgLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          space[`year${index + 1}`]="";
        nameOrgDebt[`year${index + 1}`]="";
        loanSources[`year${index + 1}`]="";
        colOrgDebt[`year${index + 1}`]=de.year;
          outstandingDebts[`year${index + 1}`] = convertToCurrencyNumber(de.outStandingDebt,2);
          disbursements[`year${index + 1}`] = convertToCurrencyNumber(de.disbursement,2);
          repayments[`year${index + 1}`] = convertToCurrencyNumber(de.repayment,2);
          principles[`year${index + 1}`] = convertToCurrencyNumber(de.principle,2);
          interests[`year${index + 1}`] = convertToCurrencyNumber(de.interest,2);
          netCashFlows[`year${index + 1}`] = convertToCurrencyNumber(de.netCashFlow,2);
        }
      );
      tableArray.push(
        space,
        nameOrgDebt,
        loanSources,
        colOrgDebt,
        outstandingDebts,
        disbursements,
        repayments,
        principles,
        interests,
        netCashFlows
      );


      //ประมาณการภาระหนี้ที่รัฐบาลต้องรับภาระ
      const nameDebtGov: any = {
        fieldname: govLocalDebtEstimationFieldName.estimateGovernmentDebt,
      };
      const loanSourcesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.domesticLoanSources,
      };
      const colDebtGov: any = {
        fieldname: govLocalDebtEstimationFieldName.list,
      };
      const principlesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.principle,
      };
      const interestsGov: any = {
        fieldname: govLocalDebtEstimationFieldName.interest,
      };
      const feesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.fee,
      };
      self.debtSummary.govLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          space[`year${index + 1}`]="";
          nameDebtGov[`year${index + 1}`]="";
          loanSourcesGov[`year${index + 1}`]="";
          colDebtGov[`year${index + 1}`]=de.year;
          principlesGov[`year${index + 1}`] = convertToCurrencyNumber(de.principle,2);
          interestsGov[`year${index + 1}`] = convertToCurrencyNumber(de.interest,2);
          feesGov[`year${index + 1}`] = convertToCurrencyNumber(de.fee,2);
        }
      );
      tableArray.push(space,nameDebtGov,loanSourcesGov,colDebtGov,principlesGov, interestsGov, feesGov);

      //อัตราส่วนทางการเงิน
      const nameFinancialRatio: any = {
        fieldname: financialRatioFieldName.financialRatio,
      };
      const colFinancialRatio: any = {
        fieldname: financialRatioFieldName.list,
      };
      const debtServiceCoverageRatios: any = {
        fieldname: financialRatioFieldName.debtServiceCoverageRato,
      };
      const ebitdasfinancialRatio: any = {
        fieldname: financialRatioFieldName.profitBeforeInterest,
      };
      const govDebtfinancialRatio: any = {
        fieldname: financialRatioFieldName.govDebt,
      };
      const pGovDebtfinancialRatio: any = {
        fieldname: financialRatioFieldName.pGovDebt,
      };
      const iGovDebtfinancialRatio: any = {
        fieldname: financialRatioFieldName.iGovDebt,
      };
      const debtsfinancialRatio: any = {
        fieldname: financialRatioFieldName.debtBurden,
      };
      const pDebtfinancialRatio: any = {
        fieldname: financialRatioFieldName.pDebt,
      };
      const pDebtGfinancialRatio: any = {
        fieldname: financialRatioFieldName.pDebtG,
      };
      const pDebtOfinancialRatio: any = {
        fieldname: financialRatioFieldName.pDebtO,
      };
      const iDebtfinancialRatio: any = {
        fieldname: financialRatioFieldName.iDebt,
      };
      const iDebtGfinancialRatio: any = {
        fieldname: financialRatioFieldName.iDebtG,
      };
      const iDebtOfinancialRatio: any = {
        fieldname: financialRatioFieldName.iDebtO,
      };
      const debtEquityRatios: any = {
        fieldname: financialRatioFieldName.debtEquityRatio,
      };
      const liabilitiesfinancialRatio: any = {
        fieldname: financialRatioFieldName.totalLiabilities,
      };
      const equitiesfinancialRatio: any = {
        fieldname: financialRatioFieldName.shareholdersEquity,
      };
      const debtTotalAssetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.debtTotalAssets,
      };
      const liabilities2financialRatio: any = {
        fieldname: financialRatioFieldName.totalLiabilities,
      };
      const assetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.totalAssets,
      };
      const workingCapitalRatios: any = {
        fieldname: financialRatioFieldName.workingCapitalRatio,
      };
      const currentAssetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.currentAssets,
      };
      const currentLiabilitiesfinancialRatio: any = {
        fieldname: financialRatioFieldName.totalCurrentLiabilities,
      };
      const returnFixedAssetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.returnFixedAssets,
      };
      const netProfitsfinancialRatio: any = {
        fieldname: financialRatioFieldName.netProfit,
      };
      const fixedAssetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.permanentAsset,
      };
      const returnTotalAssetsfinancialRatio: any = {
        fieldname: financialRatioFieldName.returnTotalAssets,
      };
      const netProfits2financialRatio: any = {
        fieldname: financialRatioFieldName.netProfit,
      };
      const assets2financialRatio: any = {
        fieldname: financialRatioFieldName.totalAssets,
      };
      const returnSalesfinancialRatio: any = {
        fieldname: financialRatioFieldName.returnSales,
      };
      const netProfits3financialRatio: any = {
        fieldname: financialRatioFieldName.netProfit,
      };
      const salesfinancialRatio: any = {
        fieldname: financialRatioFieldName.sales,
      };
      self.financialRatio.forEach((fr: IFinancialRatioModel, index: number) => {
        space[`year${index + 1}`]="";
        nameFinancialRatio[`year${index + 1}`]="";
        colFinancialRatio[`year${index + 1}`]=fr.year;
        debtServiceCoverageRatios[`year${index + 1}`] =  convertToCurrencyNumber((fr.ebida+fr.govDebt)/fr.debt,2);
        ebitdasfinancialRatio[`year${index + 1}`] = convertToCurrencyNumber(fr.ebida,2);
        govDebtfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.govDebt,2);
        pGovDebtfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.pGovDebt,2);
        iGovDebtfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.iGovDebt,2);
        debtsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.debt,2);
        pDebtfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.pDebt,2);
        pDebtGfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.pDebtG,2);
        pDebtOfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.pDebtO,2);
        iDebtfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.iDebt,2);
        iDebtGfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.iDebtG,2);
        iDebtOfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.iDebtO,2);
        debtEquityRatios[`year${index + 1}`] =  convertToCurrencyNumber(fr.leRatio,2);
        liabilitiesfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.liability,2);
        equitiesfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.equity,2);
        debtTotalAssetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.laRatio,2);
        liabilities2financialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.liability,2);
        assetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.asset,2);
        workingCapitalRatios[`year${index + 1}`] =  convertToCurrencyNumber(fr.caclRatio,2);
        currentAssetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.currentAsset,2);
        currentLiabilitiesfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.currentLiability,2);
        returnFixedAssetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.npfaRatio,2);
        netProfitsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.netProfit,2);
        fixedAssetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.fixedAsset,2);
        returnTotalAssetsfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.npaRatio,2);
        netProfits2financialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.netProfit,2);
        assets2financialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.asset,2);
        returnSalesfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.nprRatio,2);
        netProfits3financialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.netProfit,2);
        salesfinancialRatio[`year${index + 1}`] =  convertToCurrencyNumber(fr.revenue,2);
      });
      tableArray.push(
        space,
        nameFinancialRatio,
        colFinancialRatio,
        debtServiceCoverageRatios,
        ebitdasfinancialRatio,
        govDebtfinancialRatio,
        pGovDebtfinancialRatio,
        iGovDebtfinancialRatio,
        debtsfinancialRatio,
        pDebtfinancialRatio,
        pDebtGfinancialRatio,
        pDebtOfinancialRatio,
        iDebtfinancialRatio,
        iDebtGfinancialRatio,
        iDebtOfinancialRatio,
        debtEquityRatios,
        liabilitiesfinancialRatio,
        equitiesfinancialRatio,
        debtTotalAssetsfinancialRatio,
        liabilities2financialRatio,
        assetsfinancialRatio,
        workingCapitalRatios,
        currentAssetsfinancialRatio,
        currentLiabilitiesfinancialRatio,
        returnFixedAssetsfinancialRatio,
        netProfitsfinancialRatio,
        fixedAssetsfinancialRatio,
        returnTotalAssetsfinancialRatio,
        netProfits2financialRatio,
        assets2financialRatio,
        returnSalesfinancialRatio,
        netProfits3financialRatio,
        salesfinancialRatio
      );
      return tableArray;
    },
    get financialReportDataTableExcelTemplate() {
      const tableArray = [];
      const space: any = {
        fieldname: "",
      };

      const nameBalance: any = {
        fieldname: balanceSheetFieldName.preliminaryBalanceSheetProjections,
      };
      const colBalance: any = {
        fieldname: balanceSheetFieldName.list,
      };
      const assets: any = {
        fieldname: balanceSheetFieldName.asset,
      };
      const currentAssets: any = {
        fieldname: balanceSheetFieldName.currentAsset,
      };
      const fixedAssets: any = {
        fieldname: balanceSheetFieldName.fixedAsset,
      };
      const liabilityEquities: any = {
        fieldname: balanceSheetFieldName.liabilityEquity,
      };
      const liabilities: any = {
        fieldname: balanceSheetFieldName.liability,
      };
      const currentLiabilities: any = {
        fieldname: balanceSheetFieldName.currentLiability,
      };
      const longtermLiabilities: any = {
        fieldname: balanceSheetFieldName.longTermLiability,
      };
      const equities: any = {
        fieldname: balanceSheetFieldName.equity,
      };
      self.balanceSheet.forEach((bs: IBalanceSheetModel, index: number) => {
        space[`year${index + 1}`]="";
        nameBalance[`year${index + 1}`]="";
        colBalance[`year${index + 1}`]=bs.year;
        assets[`year${index + 1}`] = "";
        currentAssets[`year${index + 1}`] = convertToCurrencyNumber(0,2) ;
        fixedAssets[`year${index + 1}`] = convertToCurrencyNumber(0,2) ;
        liabilityEquities[`year${index + 1}`] ="";
        liabilities[`year${index + 1}`] = "";
        currentLiabilities[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        longtermLiabilities[`year${index + 1}`] =convertToCurrencyNumber(0,2);
        equities[`year${index + 1}`] =convertToCurrencyNumber(0,2);
      });
      tableArray.push(
        space,
        nameBalance,
        colBalance,
        assets,
        currentAssets,
        fixedAssets,
        liabilityEquities,
        liabilities,
        currentLiabilities,
        longtermLiabilities,
        equities
      );

      //Income
    
      const nameIncome: any = {
        fieldname: incomeStatementFieldName.basicProfitLossAndEstimates,
      };
      const colIncome: any = {
        fieldname: incomeStatementFieldName.list,
      };
      const revenues: any = {
        fieldname: incomeStatementFieldName.revenue,
      };
      const expenses: any = {
        fieldname: incomeStatementFieldName.expense ,
      };
      const grossProfits: any = {
        fieldname: incomeStatementFieldName.glossProfit,
      };
      const ebitdas: any = {
        fieldname: incomeStatementFieldName.ebitda,
      };
      const netProfits: any = {
        fieldname: incomeStatementFieldName.netProfit,
      };
      self.incomeStatements.forEach(
        (is: IIncomeStatementModel, index: number) => {
          space[`year${index + 1}`]="";
          nameIncome[`year${index + 1}`]="";
          colIncome[`year${index + 1}`]=is.year;
          revenues[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          expenses[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          grossProfits[`year${index + 1}`] = "";
          ebitdas[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          netProfits[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        }
      );
      tableArray.push(space,nameIncome,colIncome,revenues, expenses, grossProfits, ebitdas, netProfits);

       //cashFlowTable
       const nameCashFlow: any = {
        fieldname: cashFlowFieldName.preliminaryCashFlowStatementsEstimates,
      };
      const colCashFlow: any = {
        fieldname: cashFlowFieldName.list,
      };
      const operations: any = {
        fieldname: cashFlowFieldName.operation,
      };
      const investings: any = {
        fieldname: cashFlowFieldName.investing,
      };
      const financings: any = {
        fieldname: cashFlowFieldName.financing,
      };
      const cashflows: any = {
        fieldname: cashFlowFieldName.cashFlow,
      };
      const netCashflows: any = {
        fieldname: cashFlowFieldName.netCashFlow,
      };
      const cashBalanceBeginnings: any = {
        fieldname: cashFlowFieldName.cashBalanceBeginning,
      };
      const cashBalanceEndings: any = {
        fieldname:cashFlowFieldName.cashBalanceEnding,
      };
      self.cashFlows.forEach((is: ICashflowModel, index: number) => {
        space[`year${index + 1}`]="";
        nameCashFlow[`year${index + 1}`]="";
        colCashFlow[`year${index + 1}`]=is.year;
        operations[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        investings[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        financings[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        cashflows[`year${index + 1}`] = "";
        netCashflows[`year${index + 1}`] =  "";
        cashBalanceBeginnings[`year${index + 1}`] =convertToCurrencyNumber(0,2);
        cashBalanceEndings[`year${index + 1}`] = "";
      });
      tableArray.push(
        space,
        nameCashFlow,
        colCashFlow,
        operations,
        investings,
        financings,
        cashflows,
        netCashflows,
        cashBalanceBeginnings,
        cashBalanceEndings
      );

      //orgLocalDebtEstimation
      const nameOrgDebt: any = {
        fieldname: orgLocalDebtEstimationFieldName.estimateAllDebt,
      };
      const loanSources: any = {
        fieldname: orgLocalDebtEstimationFieldName.domesticLoanSources,
      };
      const colOrgDebt: any = {
        fieldname: orgLocalDebtEstimationFieldName.list,
      };
      const outstandingDebts: any = {
        fieldname: orgLocalDebtEstimationFieldName.outstandingDebt,
      };
      const disbursements: any = {
        fieldname: orgLocalDebtEstimationFieldName.disbursement,
      };
      const repayments: any = {
        fieldname: orgLocalDebtEstimationFieldName.repayment,
      };
      const principles: any = {
        fieldname: orgLocalDebtEstimationFieldName.principle,
      };
      const interests: any = {
        fieldname: orgLocalDebtEstimationFieldName.interest,
      };
      const netCashFlows: any = {
        fieldname: orgLocalDebtEstimationFieldName.netCashFlow,
      };
      self.debtSummary.orgLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          space[`year${index + 1}`]="";
        nameOrgDebt[`year${index + 1}`]="";
        loanSources[`year${index + 1}`]="";
        colOrgDebt[`year${index + 1}`]=de.year;
          outstandingDebts[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          disbursements[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          repayments[`year${index + 1}`] = "";
          principles[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          interests[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          netCashFlows[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        }
      );
      tableArray.push(
        space,
        nameOrgDebt,
        loanSources,
        colOrgDebt,
        outstandingDebts,
        disbursements,
        repayments,
        principles,
        interests,
        netCashFlows
      );

      //ประมาณการภาระหนี้ที่รัฐบาลต้องรับภาระ
      const nameDebtGov: any = {
        fieldname: govLocalDebtEstimationFieldName.estimateGovernmentDebt,
      };
      const loanSourcesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.domesticLoanSources,
      };
      const colDebtGov: any = {
        fieldname: govLocalDebtEstimationFieldName.list,
      };
      const principlesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.principle,
      };
      const interestsGov: any = {
        fieldname: govLocalDebtEstimationFieldName.interest,
      };
      const feesGov: any = {
        fieldname: govLocalDebtEstimationFieldName.fee,
      };
      self.debtSummary.govLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          space[`year${index + 1}`]="";
          nameDebtGov[`year${index + 1}`]="";
          loanSourcesGov[`year${index + 1}`]="";
          colDebtGov[`year${index + 1}`]=de.year;
          principlesGov[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          interestsGov[`year${index + 1}`] = convertToCurrencyNumber(0,2);
          feesGov[`year${index + 1}`] = convertToCurrencyNumber(0,2);
        }
      );
      tableArray.push(space,nameDebtGov,loanSourcesGov,colDebtGov,principlesGov, interestsGov, feesGov);
      return tableArray;
    },

    get incomeStatementDataTable() {
      const tableArray = [];
      const revenues: any = {
        label: "financial.profitAndLossTable.income",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "is",
        fieldname: "revenue",
      };
      const expenses: any = {
        label: "financial.profitAndLossTable.expenses",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "is",
        fieldname: "expense",
      };
      const grossProfits: any = {
        label: "financial.profitAndLossTable.profitLossFromOperations",
        level: 1,
        editMode: false,
        highlight: false,
        financialType: "is",
        fieldname: "glossProfit",
      };
      const ebitdas: any = {
        label: "financial.profitAndLossTable.profitLossEBITDA",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "is",
        fieldname: "ebitda",
      };
      const netProfits: any = {
        label: "financial.profitAndLossTable.netProfitLoss",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "is",
        fieldname: "netProfit",
      };
      self.incomeStatements.forEach(
        (is: IIncomeStatementModel, index: number) => {
          revenues[`year${index + 1}`] = is.revenue / million;
          expenses[`year${index + 1}`] = is.expense / million;
          grossProfits[`year${index + 1}`] = is.grossProfitView
            ? is.grossProfitView
            : is.glossProfit / million;
          ebitdas[`year${index + 1}`] = is.ebitda / million;
          netProfits[`year${index + 1}`] = is.netProfit / million;
        }
      );
      tableArray.push(revenues, expenses, grossProfits, ebitdas, netProfits);
      return tableArray;
    },
    get cashflowDataTable() {
      const tableArray = [];
      const operations: any = {
        label: "financial.cashFlowTable.cashFlowFromOperations",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "cf",
        fieldname: "operation",
      };
      const investings: any = {
        label: "financial.cashFlowTable.cashFlowFromInvestment",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "cf",
        fieldname: "investing",
      };
      const financings: any = {
        label: "financial.cashFlowTable.cashFlowFromFinancing",
        level: 1,
        editMode: true,
        highlight: false,
        financialType: "cf",
        fieldname: "financing",
      };
      const cashflows: any = {
        label: "financial.cashFlowTable.cashFlow",
        level: 1,
        editMode: false,
        highlight: false,
        emptyString: true,
      };
      const netCashflows: any = {
        label: "financial.cashFlowTable.netCashReceivedLessthanCashPaid",
        level: 2,
        editMode: false,
        highlight: false,
        financialType: "cf",
        fieldname: "netCashFlow",
      };
      const cashBalanceBeginnings: any = {
        label: "financial.cashFlowTable.cashBalanceBeginningPeriod",
        level: 2,
        editMode: true,
        highlight: false,
        financialType: "cf",
        fieldname: "cashBalanceBeginning",
      };
      const cashBalanceEndings: any = {
        label: "financial.cashFlowTable.cashBalanceEndPeriod",
        level: 2,
        editMode: false,
        highlight: false,
        financialType: "cf",
        fieldname: "cashBalanceEnding",
      };
      self.cashFlows.forEach((is: ICashflowModel, index: number) => {
        operations[`year${index + 1}`] = is.operation / million;
        investings[`year${index + 1}`] = is.investing / million;
        financings[`year${index + 1}`] = is.financing / million;
        cashflows[`year${index + 1}`] = "";
        netCashflows[`year${index + 1}`] = is.netCashFlowView
          ? is.netCashFlowView
          : is.netCashFlow / million;
        cashBalanceBeginnings[`year${index + 1}`] =
          is.cashBalanceBeginning / million;
        cashBalanceEndings[`year${index + 1}`] = is.cashBalanceEndingView
          ? is.cashBalanceEndingView
          : is.cashBalanceEnding / million;
      });
      tableArray.push(
        operations,
        investings,
        financings,
        cashflows,
        netCashflows,
        cashBalanceBeginnings,
        cashBalanceEndings
      );
      return tableArray;
    },
    get estimateAllDebtDataTable() {
      const tableArray = [];
      const outstandingDebts: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.outstandingDebt",
        editMode: true,
        financialType: "ead",
        fieldname: "outStandingDebt",
      };
      const disbursements: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.disbursement",
        editMode: true,
        financialType: "ead",
        fieldname: "disbursement",
      };
      const repayments: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.repayment",
      };
      const principles: any = {
        level: 2,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.principle",
        editMode: true,
        financialType: "ead",
        fieldname: "principle",
      };
      const interests: any = {
        level: 2,
        label: "financial.OrganizationDebtEstimation.estimateAllDeb.interest",
        editMode: true,
        financialType: "ead",
        fieldname: "interest",
      };
      const netCashFlows: any = {
        level: 1,
        label:
          "financial.OrganizationDebtEstimation.estimateAllDeb.netCashFlow",
        editMode: true,
        financialType: "ead",
        fieldname: "netCashFlow",
      };
      self.debtSummary.orgLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          outstandingDebts[`year${index + 1}`] = de.outStandingDebt / million;
          disbursements[`year${index + 1}`] = de.disbursement / million;
          repayments[`year${index + 1}`] = de.repayment / million;
          principles[`year${index + 1}`] = de.principle / million;
          interests[`year${index + 1}`] = de.interest / million;
          netCashFlows[`year${index + 1}`] = de.netCashFlow / million;
        }
      );
      tableArray.push(
        outstandingDebts,
        disbursements,
        repayments,
        principles,
        interests,
        netCashFlows
      );

      return tableArray;
    },
    get estimateGovernmentDebtDataTable() {
      const tableArray = [];
      const principles: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.principle",
        editMode: true,
        financialType: "egd",
        fieldname: "principle",
      };
      const interests: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.interest",
        editMode: true,
        financialType: "egd",
        fieldname: "interest",
      };
      const fees: any = {
        level: 1,
        label: "financial.OrganizationDebtEstimation.estimateGovDebt.fee",
        editMode: true,
        financialType: "egd",
        fieldname: "fee",
      };
      self.debtSummary.govLocalDebtEstimation.forEach(
        (de: IDebtEstimateModel, index: number) => {
          principles[`year${index + 1}`] = de.principle / million;
          interests[`year${index + 1}`] = de.interest / million;
          fees[`year${index + 1}`] = de.fee / million;
        }
      );
      tableArray.push(principles, interests, fees);
      return tableArray;
    },
    get financialRatioDataTable() {
      const tableArray = [];
      const debtServiceCoverageRatios: any = {
        label: "financial.financialRatioTable.debtServiceCoverageRato",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const ebitdas: any = {
        label: "financial.financialRatioTable.profitBeforeInterest",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const govDebt: any = {
        label: "financial.financialRatioTable.govDebt",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const pGovDebt: any = {
        label: "financial.financialRatioTable.pGovDebt",
        level: 2,
        editMode: false,
        highlight: false,
      };
      const iGovDebt: any = {
        label: "financial.financialRatioTable.iGovDebt",
        level: 2,
        editMode: false,
        highlight: false,
      };
      const debts: any = {
        label: "financial.financialRatioTable.debtBurden",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const pDebt: any = {
        label: "financial.financialRatioTable.pDebt",
        level: 2,
        editMode: false,
        highlight: false,
      };
      const pDebtG: any = {
        label: "financial.financialRatioTable.pDebtG",
        level: 3,
        editMode: false,
        highlight: false,
      };
      const pDebtO: any = {
        label: "financial.financialRatioTable.pDebtO",
        level: 3,
        editMode: false,
        highlight: false,
      };
      const iDebt: any = {
        label: "financial.financialRatioTable.iDebt",
        level: 2,
        editMode: false,
        highlight: false,
      };
      const iDebtG: any = {
        label: "financial.financialRatioTable.iDebtG",
        level: 3,
        editMode: false,
        highlight: false,
      };
      const iDebtO: any = {
        label: "financial.financialRatioTable.iDebtO",
        level: 3,
        editMode: false,
        highlight: false,
      };
      const debtEquityRatios: any = {
        label: "financial.financialRatioTable.debtEquityRatio",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const liabilities: any = {
        label: "financial.financialRatioTable.totalLiabilities",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const equities: any = {
        label: "financial.financialRatioTable.shareholdersEquity",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const debtTotalAssets: any = {
        label: "financial.financialRatioTable.debtTotalAssets",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const liabilities2: any = {
        label: "financial.financialRatioTable.totalLiabilities",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const assets: any = {
        label: "financial.financialRatioTable.totalAssets",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const workingCapitalRatios: any = {
        label: "financial.financialRatioTable.workingCapitalRatio",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const currentAssets: any = {
        label: "financial.financialRatioTable.currentAssets",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const currentLiabilities: any = {
        label: "financial.financialRatioTable.totalCurrentLiabilities",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const returnFixedAssets: any = {
        label: "financial.financialRatioTable.returnFixedAssets",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const netProfits: any = {
        label: "financial.financialRatioTable.netProfit",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const fixedAssets: any = {
        label: "financial.financialRatioTable.permanentAsset",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const returnTotalAssets: any = {
        label: "financial.financialRatioTable.returnTotalAssets",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const netProfits2: any = {
        label: "financial.financialRatioTable.netProfit",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const assets2: any = {
        label: "financial.financialRatioTable.totalAssets",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const returnSales: any = {
        label: "financial.financialRatioTable.returnSales",
        level: 1,
        editMode: false,
        highlight: true,
      };
      const netProfits3: any = {
        label: "financial.financialRatioTable.netProfit",
        level: 1,
        editMode: false,
        highlight: false,
      };
      const sales: any = {
        label: "financial.financialRatioTable.sales",
        level: 1,
        editMode: false,
        highlight: false,
      };
      self.financialRatio.forEach((fr: IFinancialRatioModel, index: number) => {
        debtServiceCoverageRatios[`year${index + 1}`] = (fr.ebida+fr.govDebt)/fr.debt;
        ebitdas[`year${index + 1}`] = fr.ebida / million;
        govDebt[`year${index + 1}`] = fr.govDebt / million;
        pGovDebt[`year${index + 1}`] = fr.pGovDebt / million;
        iGovDebt[`year${index + 1}`] = fr.iGovDebt / million;
        debts[`year${index + 1}`] = fr.debt / million;
        pDebt[`year${index + 1}`] = fr.pDebt / million;
        pDebtG[`year${index + 1}`] = fr.pDebtG / million;
        pDebtO[`year${index + 1}`] = fr.pDebtO / million;
        iDebt[`year${index + 1}`] = fr.iDebt / million;
        iDebtG[`year${index + 1}`] = fr.iDebtG / million;
        iDebtO[`year${index + 1}`] = fr.iDebtO / million;
        debtEquityRatios[`year${index + 1}`] = fr.leRatio;
        liabilities[`year${index + 1}`] = fr.liability / million;
        equities[`year${index + 1}`] = fr.equity / million;
        debtTotalAssets[`year${index + 1}`] = fr.laRatio;
        liabilities2[`year${index + 1}`] = fr.liability / million;
        assets[`year${index + 1}`] = fr.asset / million;
        workingCapitalRatios[`year${index + 1}`] = fr.caclRatio;
        currentAssets[`year${index + 1}`] = fr.currentAsset / million;
        currentLiabilities[`year${index + 1}`] = fr.currentLiability / million;
        returnFixedAssets[`year${index + 1}`] = fr.npfaRatio;
        netProfits[`year${index + 1}`] = fr.netProfit / million;
        fixedAssets[`year${index + 1}`] = fr.fixedAsset / million;
        returnTotalAssets[`year${index + 1}`] = fr.npaRatio;
        netProfits2[`year${index + 1}`] = fr.netProfit / million;
        assets2[`year${index + 1}`] = fr.asset / million;
        returnSales[`year${index + 1}`] = fr.nprRatio;
        netProfits3[`year${index + 1}`] = fr.netProfit / million;
        sales[`year${index + 1}`] = fr.revenue / million;
      });
      tableArray.push(
        debtServiceCoverageRatios,
        ebitdas,
        govDebt,
        pGovDebt,
        iGovDebt,
        debts,
        pDebt,
        pDebtG,
        pDebtO,
        iDebt,
        iDebtG,
        iDebtO,
        debtEquityRatios,
        liabilities,
        equities,
        debtTotalAssets,
        liabilities2,
        assets,
        workingCapitalRatios,
        currentAssets,
        currentLiabilities,
        returnFixedAssets,
        netProfits,
        fixedAssets,
        returnTotalAssets,
        netProfits2,
        assets2,
        returnSales,
        netProfits3,
        sales
      );
      return tableArray;
    },
    // modify by ma2022 สร้างเพื่อมาให้แก้ไขอัตราทางการเงินได้ แต่ไม่เอาแล้ว
    // get EditfinancialRatioDataTable() {
    //   const tableArray = [];
    //   const debtServiceCoverageRatios: any = {
    //     label: "financial.financialRatioTable.debtServiceCoverageRato",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "dsrc",
    //   };
    //   const ebitdas: any = {
    //     label: "financial.financialRatioTable.profitBeforeInterest",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "ebida",
    //   };
    //   const govDebt: any = {
    //     label: "financial.financialRatioTable.govDebt",
    //     level: 1,
    //     editMode: false,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "govDebt",
    //   };
    //   const pGovDebt: any = {
    //     label: "financial.financialRatioTable.pGovDebt",
    //     level: 2,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "pGovDebt",
    //   };
    //   const iGovDebt: any = {
    //     label: "financial.financialRatioTable.iGovDebt",
    //     level: 2,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "iGovDebt",
    //   };
    //   const debts: any = {
    //     label: "financial.financialRatioTable.debtBurden",
    //     level: 1,
    //     editMode: false,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "debt",
    //   };
    //   const pDebt: any = {
    //     label: "financial.financialRatioTable.pDebt",
    //     level: 2,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "pDebt",
    //   };
    //   const pDebtG: any = {
    //     label: "financial.financialRatioTable.pDebtG",
    //     level: 3,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "pDebtG",
    //   };
    //   const pDebtO: any = {
    //     label: "financial.financialRatioTable.pDebtO",
    //     level: 3,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "pDebtO",
    //   };
    //   const iDebt: any = {
    //     label: "financial.financialRatioTable.iDebt",
    //     level: 2,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "iDebt",
    //   };
    //   const iDebtG: any = {
    //     label: "financial.financialRatioTable.iDebtG",
    //     level: 3,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "iDebtG",
    //   };
    //   const iDebtO: any = {
    //     label: "financial.financialRatioTable.iDebtO",
    //     level: 3,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "iDebtO",
    //   };
    //   const debtEquityRatios: any = {
    //     label: "financial.financialRatioTable.debtEquityRatio",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "leRatio",
    //   };
    //   const liabilities: any = {
    //     label: "financial.financialRatioTable.totalLiabilities",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "liability",
    //   };
    //   const equities: any = {
    //     label: "financial.financialRatioTable.shareholdersEquity",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "equity",
    //   };
    //   const debtTotalAssets: any = {
    //     label: "financial.financialRatioTable.debtTotalAssets",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "laRatio",
    //   };
    //   const liabilities2: any = {
    //     label: "financial.financialRatioTable.totalLiabilities",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "liability",
    //   };
    //   const assets: any = {
    //     label: "financial.financialRatioTable.totalAssets",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "asset",
    //   };
    //   const workingCapitalRatios: any = {
    //     label: "financial.financialRatioTable.workingCapitalRatio",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "caclRatio",
    //   };
    //   const currentAssets: any = {
    //     label: "financial.financialRatioTable.currentAssets",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "currentAsset",
    //   };
    //   const currentLiabilities: any = {
    //     label: "financial.financialRatioTable.totalCurrentLiabilities",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "currentLiability",
    //   };
    //   const returnFixedAssets: any = {
    //     label: "financial.financialRatioTable.returnFixedAssets",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "npfaRatio",
    //   };
    //   const netProfits: any = {
    //     label: "financial.financialRatioTable.netProfit",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "netProfit",
    //   };
    //   const fixedAssets: any = {
    //     label: "financial.financialRatioTable.permanentAsset",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "fixedAsset",
    //   };
    //   const returnTotalAssets: any = {
    //     label: "financial.financialRatioTable.returnTotalAssets",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "npaRatio",
    //   };
    //   const netProfits2: any = {
    //     label: "financial.financialRatioTable.netProfit",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "netProfit",
    //   };
    //   const assets2: any = {
    //     label: "financial.financialRatioTable.totalAssets",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "asset",
    //   };
    //   const returnSales: any = {
    //     label: "financial.financialRatioTable.returnSales",
    //     level: 1,
    //     editMode: false,
    //     highlight: true,
    //     financialType: "efr",
    //     fieldname: "nprRatio",
    //   };
    //   const netProfits3: any = {
    //     label: "financial.financialRatioTable.netProfit",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "netProfit",
    //   };
    //   const sales: any = {
    //     label: "financial.financialRatioTable.sales",
    //     level: 1,
    //     editMode: true,
    //     highlight: false,
    //     financialType: "efr",
    //     fieldname: "revenue",
    //   };
    //   self.editFinRatio.financialRatio.forEach((fr: IFinancialRatioModel, index: number) => {
    //     debtServiceCoverageRatios[`year${index + 1}`] = fr.ebida/(fr.pDebt + fr.pDebtG+ fr.pDebtO+fr.iDebt+fr.iDebtG+fr.iDebtO);
    //     ebitdas[`year${index + 1}`] = fr.ebida / million;
    //     govDebt[`year${index + 1}`] =  fr.govDebtView
    //     ? fr.govDebtView
    //     : fr.govDebt / million;
    //     pGovDebt[`year${index + 1}`] = fr.pGovDebt / million;
    //     iGovDebt[`year${index + 1}`] = fr.iGovDebt / million;
    //     debts[`year${index + 1}`] = fr.debtView
    //     ? fr.debtView
    //     : fr.debt / million;
    //     pDebt[`year${index + 1}`] = fr.pDebt / million;
    //     pDebtG[`year${index + 1}`] = fr.pDebtG / million;
    //     pDebtO[`year${index + 1}`] = fr.pDebtO / million;
    //     iDebt[`year${index + 1}`] = fr.iDebt / million;
    //     iDebtG[`year${index + 1}`] = fr.iDebtG / million;
    //     iDebtO[`year${index + 1}`] = fr.iDebtO / million;
    //     debtEquityRatios[`year${index + 1}`] = fr.liability/fr.equity;
    //     liabilities[`year${index + 1}`] = fr.liability / million;
    //     equities[`year${index + 1}`] = fr.equity / million;
    //     debtTotalAssets[`year${index + 1}`] = fr.liability/fr.asset;
    //     liabilities2[`year${index + 1}`] = fr.liability / million;
    //     assets[`year${index + 1}`] = fr.asset / million;
    //     workingCapitalRatios[`year${index + 1}`] = fr.currentAsset/fr.currentLiability;
    //     currentAssets[`year${index + 1}`] = fr.currentAsset / million;
    //     currentLiabilities[`year${index + 1}`] = fr.currentLiability / million;
    //     returnFixedAssets[`year${index + 1}`] = fr.netProfit/fr.fixedAsset;
    //     netProfits[`year${index + 1}`] = fr.netProfit / million;
    //     fixedAssets[`year${index + 1}`] = fr.fixedAsset / million;
    //     returnTotalAssets[`year${index + 1}`] = fr.netProfit/fr.asset;
    //     netProfits2[`year${index + 1}`] = fr.netProfit / million;
    //     assets2[`year${index + 1}`] = fr.asset / million;
    //     returnSales[`year${index + 1}`] = fr.netProfit/fr.revenue;
    //     netProfits3[`year${index + 1}`] = fr.netProfit / million;
    //     sales[`year${index + 1}`] = fr.revenue / million;
    //   });
    //   tableArray.push(
    //     debtServiceCoverageRatios,
    //     ebitdas,
    //     govDebt,
    //     pGovDebt,
    //     iGovDebt,
    //     debts,
    //     pDebt,
    //     pDebtG,
    //     pDebtO,
    //     iDebt,
    //     iDebtG,
    //     iDebtO,
    //     debtEquityRatios,
    //     liabilities,
    //     equities,
    //     debtTotalAssets,
    //     liabilities2,
    //     assets,
    //     workingCapitalRatios,
    //     currentAssets,
    //     currentLiabilities,
    //     returnFixedAssets,
    //     netProfits,
    //     fixedAssets,
    //     returnTotalAssets,
    //     netProfits2,
    //     assets2,
    //     returnSales,
    //     netProfits3,
    //     sales
    //   );
    //   return tableArray;
    // },
    get govLocalDebtChartData() {
    // ตรวจสอบว่า govLocalDebtEstimation เป็น array
      if (!self.govLocalDebtEstimation) return [];
      return self.govLocalDebtEstimation.map((item: any) => ({
        year: item.year,
        debtValue: (item.principle || 0) / 1000000,        // หน่วย: ล้านบาท
        outstandingValue: (item.interest || 0) / 1000000,  // หน่วย: ล้านบาท
      }));
    },
    get cashflowChartData() {
      // ตรวจสอบว่า cashFlows มีข้อมูลหรือไม่
      console.log("cashFlows ใน getter:", self.cashFlows);
      
      if (!self.cashFlows) {
        console.log("cashFlows เป็น undefined หรือ null");
        return [];
      }
      
      if (!Array.isArray(self.cashFlows)) {
        console.log("cashFlows ไม่ใช่อาร์เรย์, เป็น:", typeof self.cashFlows);
        // ถ้า cashFlows เป็น object แทนที่จะเป็นอาร์เรย์
        if (typeof self.cashFlows === 'object') {
          // ลอง convert เป็นอาร์เรย์
          console.log("ลอง convert Object เป็น Array");
          const cashFlowsArray = Object.values(self.cashFlows);
          console.log("cashFlowsArray:", cashFlowsArray);
          
          return cashFlowsArray.map((item: any) => ({
            year: item.year,
            value: Number(item.financing || 0) + Number(item.investing || 0) + Number(item.operation || 0),
          }));
        }
        return [];
      }
      
      if (self.cashFlows.length === 0) {
        console.log("cashFlows เป็นอาร์เรย์ว่าง");
        return [];
      }
      
      // ตรวจสอบโครงสร้างข้อมูลในอาร์เรย์
      console.log("ตัวอย่างข้อมูลแรก:", self.cashFlows[0]);
      
      return self.cashFlows.map((item: any) => {
        console.log("กำลังแปลงข้อมูล:", item);
        return {
          year: item.year || "",
          value: Number(item.financing || 0) + Number(item.investing || 0) + Number(item.operation || 0),
        };
      });
    }
  }))
  .actions((self: any) => ({
    setCashFlows(data: any) {
      if (Array.isArray(data)) {
        self.cashFlows = data;
      }
    },
    getFinancialReportDashboard: flow(function* () {
      try {
        self.loading = true;
        const result = yield financialReportAPI.getFinancialReportDashboard({
          organizationID: self.organizationID,
          planRelease: self.planRelease,
          startYear: self.year,
          planId: self.planID
        });
        console.log("API response full:", result);
        console.log("API response data:", result.data);
        
        if (result.data && result.data.cashFlows) {
          self.setCashFlows(result.data.cashFlows);
        }
        self.debtSummary = result.data.debtSummary; // self.debtSummary.orgLocalDebtEstimation
        self.orgLocalDebtEstimation = result.data.debtSummary.orgLocalDebtEstimation;
        self.financialRatio = result.data.financialRatio;
        
      } catch (e) {
        // handle error
      } finally {
        self.loading = false;
      }
    }),
    getFinancialReport: flow(function* () {
      try {
        self.loading = true;
        const result = yield financialReportAPI.getFinancialReport(
          self.planHeader.planID
        );
        console.log('financialReportAPI .data',result.data)
        const {
          balanceSheet,
          incomeStatements,
          cashFlows,
          debtSummary,
          financialRatio,
          editFinRatio,
          ...rest
        } = result.data;
        self.setFieldByOrder(balanceSheet, "balanceSheet", "year");
        self.setFieldByOrder(incomeStatements, "incomeStatements", "year");
        self.setFieldByOrder(cashFlows, "cashFlows", "year");
        self.setFieldByOrder(financialRatio, "financialRatio", "year");
        self.setField({ fieldname: "editFinRatio", value: editFinRatio });
        self.setDebtSummary(debtSummary);
        self.setAllField(rest);
        if (self.balanceSheet.length === 0) {
          self.createBalanceSheet();
        }else if(self.balanceSheet.length === 8){
          self.AddCreateBalanceSheet();
        }
        if (self.incomeStatements.length === 0) {
          self.createIncomeStatements();
        }else if (self.incomeStatements.length === 8) {
          self.AddCreateIncomeStatements();
        }
        if (self.cashFlows.length === 0) {
          self.createCashflows();
        }else if (self.cashFlows.length === 8) {
          self.AddCreateCashflows();
        }
        if (self.debtSummary.orgLocalDebtEstimation.length === 0) {
          self.createOrgLocalDebtEstimation();
        }else if (self.debtSummary.orgLocalDebtEstimation.length === 8) {
          self.AddCreateOrgLocalDebtEstimation();
        }
        if (self.debtSummary.govLocalDebtEstimation.length === 0) {
          self.createGovLocalDebtEstimation();
        }else if (self.debtSummary.govLocalDebtEstimation.length === 8) {
          self.AddCreateGovLocalDebtEstimation();
        }
        if (self.debtSummary.orgForeignDebtEstimationList.length === 0) {
          self.createOrgForeignDebtEstimationList();
        }else if (self.debtSummary.orgForeignDebtEstimationList.length > 0) {
          self.debtSummary.orgForeignDebtEstimationList.forEach((item: IDebtEstimateListModel) => {
                if (item.orgForeignDebtEstimation.length ==8) {
                  item.orgForeignDebtEstimation.push({ year: `${self.planHeader.planYear - 3}` });
                  const orgForeignDebtEstimation =item.orgForeignDebtEstimation;
                  item.orgForeignDebtEstimation = self.sortBy(
                    orgForeignDebtEstimation,
                    "year"
                  );
                }
          });
        }
        if (self.debtSummary.govForeignDebtEstimationList.length === 0) {
          self.createGovForeignDebtEstimationList();
        }else if (self.debtSummary.govForeignDebtEstimationList.length > 0) {
          self.debtSummary.govForeignDebtEstimationList.forEach((item: IGovDebtEstimateListModel) => {
                if (item.govForeignDebtEstimation.length ==8) {
                  item.govForeignDebtEstimation.push({ year: `${self.planHeader.planYear - 3}` });
                  const govForeignDebtEstimation =item.govForeignDebtEstimation;
                  item.govForeignDebtEstimation = self.sortBy(
                    govForeignDebtEstimation,
                    "year"
                  );
                }
          });
        }
        // if (self.dscrNotes.length === 0) {
        //   const dscrData: any = [];
        //   self.financialRatio.forEach((fr: IFinancialRatioModel) => {
        //     if (fr.dsrc < 1) {
        //       dscrData.push({ year: fr.year, dsrc: `${fr.dsrc}` });
        //     }
        //   });
        //   self.dscrNotes = dscrData;
        // }
        if (self.financialRatio.length === 0) {
          self.createFinancialRatio();
        }else if (self.financialRatio.length === 8) {
          self.AddCreateFinancialRatio();
        }
         // modify by ma2022 สร้างเพื่อมาให้แก้ไขอัตราทางการเงินได้ แต่ไม่เอาแล้ว
        // if (self.editFinRatio.financialRatio.length === 0) {
        //   self.createEditFinRatio();
        // }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
        self.loading = false;
      }
    }),
    createFinancialReport: flow(function* () {
      try {
        const result = yield financialReportAPI.create({
          planRelease: self.planHeader.planRelease,
          startYear: self.planHeader.planYear,
          organizationID: self.organization.id,
        });
        if (result) {
          self.planHeader.setField({ fieldname: "planID", value: result.data });
        }
        return result;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    updateFinancialReport: flow(function* () {
      try {
        self.loading = true;
        yield financialReportAPI.updateFinancialReport(
          {
            planHeader: self.planHeader,
            balanceSheet: self.balanceSheet,
            incomeStatements: self.incomeStatements,
            cashFlows: self.cashFlows,
            debtSummary: self.debtSummary,
            // financialRatio: self.financialRatio,
            // editFinRatio:self.editFinRatio,
            dscrNotes: self.dscrNotes,
            coffers: self.coffers,
            attachFiles: self.attachFiles,
          },
          self.planHeader.planID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
        self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
        yield self.getFinancialReport();
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    deleteDscr: flow(function* (reason: IDSCRNoteModel) {
      try {
        self.removeDSCRReason(reason);
        yield self.updateFinancialReport();
      } catch (e) {
        console.log(e);
      }
    }),
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log(e);
          console.log("exception on mapping key:", key);
        }
      });
    },
    setFieldByOrder: (data: any, fieldname: string, orderBy: string) => {
      self[fieldname] = self.sortBy(data, orderBy);
    },
    sortBy: (data: any, orderBy: string) => {
      return data.sort((a: any, b: any) => (a[orderBy] > b[orderBy] ? 1 : -1));
    },
    setFieldDebtSummaryByOrder: (data: any, fieldname: string, orderBy: string) => {
      self.debtSummary[fieldname] = self.sortBy(
        data,
        "year"
      );
    },
    setDebtSummary: (debtSummary: any) => {
      const {
        currentDebt,
        orgLocalDebtEstimation,
        orgForeignDebtEstimationList,
        govLocalDebtEstimation,
        govForeignDebtEstimationList,
      } = debtSummary;
      self.debtSummary.currentDebt = currentDebt;
      self.debtSummary.orgLocalDebtEstimation = self.sortBy(
        orgLocalDebtEstimation,
        "year"
      );
      self.debtSummary.govLocalDebtEstimation = self.sortBy(
        govLocalDebtEstimation,
        "year"
      );
      self.debtSummary.orgForeignDebtEstimationList = orgForeignDebtEstimationList.map(
        (item: any) => {
          return {
            currency: item.currency,
            orgForeignDebtEstimation: self.sortBy(
              item.orgForeignDebtEstimation,
              "year"
            ),
          };
        }
      );
      self.debtSummary.govForeignDebtEstimationList = govForeignDebtEstimationList.map(
        (item: any) => {
          return {
            currency: item.currency,
            govForeignDebtEstimation: self.sortBy(
              item.govForeignDebtEstimation,
              "year"
            ),
          };
        }
      );
    },
    setPlanId: (id: number) => {
      self.planHeader.planID = id;
    },
    onAddFile: (fieldname: "attachFiles", file: File[]) => {
      file.forEach((_file: File) => {
        try {
          const newFileUpload: IFileModel = FileModel.create({});
          newFileUpload.setToBase64Format(_file);
          self.onAddItem(fieldname, newFileUpload);
        } catch (e) {
          console.log("onAddFile Error: ", e);
        }
      });
    },
    onRemoveFile: (file: IFileModel) => {
      const index = self.attachFiles.findIndex(
        (_file: IFileModel) => _file.fileName === file.fileName
      );
      if (index >= 0) {
        self.attachFiles.splice(index, 1);
      }
    },
    onAddItem: (fieldname: string, value: any) => {
      self[fieldname].push(value);
    },
    addNewDSCRReason: (reason: IDSCRNoteModel) => {
      self.dscrNotes.push(reason);
    },
    removeDSCRReason: (reason: IDSCRNoteModel) => {
      detach(reason);
    },
    resetAll: () => {
      applySnapshot(self, {});
    },
    createBalanceSheet: () => {
      self.balanceSheet.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateBalanceSheet: () => {
      self.balanceSheet.push(
        { year: `${self.planHeader.planYear - 3}` }
      );
      self.setFieldByOrder(self.balanceSheet, "balanceSheet", "year");
    },
    createIncomeStatements: () => {
      self.incomeStatements.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateIncomeStatements: () => {
      self.incomeStatements.push(
        { year: `${self.planHeader.planYear - 3}` }
      );
      self.setFieldByOrder(self.incomeStatements, "incomeStatements", "year");
    },
    createCashflows: () => {
      self.cashFlows.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateCashflows: () => {
      self.cashFlows.push(
        { year: `${self.planHeader.planYear - 3}` },
      );
      self.setFieldByOrder(self.cashFlows, "cashFlows", "year");
    },
    createOrgLocalDebtEstimation: () => {
      self.debtSummary.orgLocalDebtEstimation.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateOrgLocalDebtEstimation: () => {
      self.debtSummary.orgLocalDebtEstimation.push(
        { year: `${self.planHeader.planYear - 3}` }
      );
      const orgLocalDebtEstimation =self.debtSummary.orgLocalDebtEstimation;
      self.debtSummary.orgLocalDebtEstimation = self.sortBy(
        orgLocalDebtEstimation,
        "year"
      );
      },
    createGovLocalDebtEstimation: () => {
      self.debtSummary.govLocalDebtEstimation.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateGovLocalDebtEstimation: () => {
      self.debtSummary.govLocalDebtEstimation.push(
        { year: `${self.planHeader.planYear - 3}` }
      );
      const govLocalDebtEstimation =self.debtSummary.govLocalDebtEstimation;
      self.debtSummary.govLocalDebtEstimation = self.sortBy(
        govLocalDebtEstimation,
        "year"
      );
    },
    createOrgForeignDebtEstimationList: () => {
      self.debtSummary.orgForeignDebtEstimationList.push({
        currency: "USD",
        orgForeignDebtEstimation: [
          { year: `${self.planHeader.planYear - 3}` },
          { year: `${self.planHeader.planYear - 2}` },
          { year: `${self.planHeader.planYear - 1}` },
          { year: `${self.planHeader.planYear}` },
          { year: `${self.planHeader.planYear + 1}` },
          { year: `${self.planHeader.planYear + 2}` },
          { year: `${self.planHeader.planYear + 3}` },
          { year: `${self.planHeader.planYear + 4}` },
          { year: `${self.planHeader.planYear + 5}` },
        ],
      });
    },
    AddCreateOrgForeignDebtEstimationList: () => {
      
      const orgForeignDebtEstimationData =self.debtSummary.orgForeignDebtEstimationList.orgForeignDebtEstimation;
      const currency =self.debtSummary.orgForeignDebtEstimationList.currency;
      self.debtSummary.orgForeignDebtEstimationList.push({
        currency: currency,
        orgForeignDebtEstimation: orgForeignDebtEstimationData,
      });
    },
    createGovForeignDebtEstimationList: () => {
      self.debtSummary.govForeignDebtEstimationList.push({
        currency: "USD",
        govForeignDebtEstimation: [
          { year: `${self.planHeader.planYear - 3}` },
          { year: `${self.planHeader.planYear - 2}` },
          { year: `${self.planHeader.planYear - 1}` },
          { year: `${self.planHeader.planYear}` },
          { year: `${self.planHeader.planYear + 1}` },
          { year: `${self.planHeader.planYear + 2}` },
          { year: `${self.planHeader.planYear + 3}` },
          { year: `${self.planHeader.planYear + 4}` },
          { year: `${self.planHeader.planYear + 5}` },
        ],
      });
    },
    createFinancialRatio: () => {
      self.financialRatio.push(
        { year: `${self.planHeader.planYear - 3}` },
        { year: `${self.planHeader.planYear - 2}` },
        { year: `${self.planHeader.planYear - 1}` },
        { year: `${self.planHeader.planYear}` },
        { year: `${self.planHeader.planYear + 1}` },
        { year: `${self.planHeader.planYear + 2}` },
        { year: `${self.planHeader.planYear + 3}` },
        { year: `${self.planHeader.planYear + 4}` },
        { year: `${self.planHeader.planYear + 5}` }
      );
    },
    AddCreateFinancialRatio: () => {
      self.financialRatio.push(
        { year: `${self.planHeader.planYear - 3}` },
      );
      self.setFieldByOrder(self.financialRatio, "financialRatio", "year");
    },
     // modify by ma2022 สร้างเพื่อมาให้แก้ไขอัตราทางการเงินได้ แต่ไม่เอาแล้ว
    // createEditFinRatio: () => {
    //   self.editFinRatio.financialRatio.push(
    //     { year: `${self.planHeader.planYear - 2}` },
    //     { year: `${self.planHeader.planYear - 1}` },
    //     { year: `${self.planHeader.planYear}` },
    //     { year: `${self.planHeader.planYear + 1}` },
    //     { year: `${self.planHeader.planYear + 2}` },
    //     { year: `${self.planHeader.planYear + 3}` },
    //     { year: `${self.planHeader.planYear + 4}` },
    //     { year: `${self.planHeader.planYear + 5}` }
    //   );
    // },
  }));
export type IFinancialReportModel = typeof FinancialReportModel.Type;
